<template>
  <div
    :class="[
      embedded ? 'cms-input-embedded' : 'cms-form-group',
      { 'cms-form-group-full': fullWidth && !embedded, 'cms-disabled': disabled }
    ]"
  >
    <label v-if="label && embedded" :for="inputId" class="cms-input-sr-only">{{ label }}</label>
    <div v-if="(label || helpText) && !embedded" class="cms-input-label-wrapper">
      <label v-if="label" :for="inputId">{{ label }}</label>
      <div v-if="helpText" class="cms-help-tooltip-wrapper">
        <div class="cms-help-tooltip" @mouseenter="showTooltip = true" @mouseleave="showTooltip = false">
          <v-icon icon="question" size="sm" class="cms-help-icon" />
          <div v-if="showTooltip" class="cms-tooltip-content">
            <div class="cms-tooltip-arrow"></div>
            <div class="cms-tooltip-text" v-html="helpText"></div>
          </div>
        </div>
      </div>
    </div>
    <input
      :id="inputId"
      :type="type"
      :value="modelValue"
      :placeholder="placeholder"
      :disabled="disabled"
      :required="required"
      :step="step"
      :inputmode="type === 'number' && allowDecimal ? 'decimal' : undefined"
      class="cms-input"
      @input="handleInput"
    />
    <span v-if="helpText && !showTooltipOnHover && !embedded" class="cms-form-help">{{ helpText }}</span>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import VIcon from '@/components/vendor/basic/icon/VIcon.vue';

interface Props {
  modelValue: string | number;
  label?: string;
  type?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  embedded?: boolean;
  helpText?: string;
  showTooltipOnHover?: boolean;
  /** When true, number inputs accept one decimal point (e.g. 17.4). */
  allowDecimal?: boolean;
  step?: string | number;
}

const props = withDefaults(defineProps<Props>(), {
  type: 'text',
  placeholder: '',
  disabled: false,
  required: false,
  fullWidth: false,
  embedded: false,
  showTooltipOnHover: true,
  allowDecimal: false,
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
}>();

const inputId = computed(() => `cms-input-${Math.random().toString(36).substr(2, 9)}`);
const showTooltip = ref(false);

const handleInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  let value = target.value;
  
  if (props.type === 'number') {
    if (props.allowDecimal) {
      value = value.replace(/[^\d.]/g, '');
      const parts = value.split('.');
      if (parts.length > 2) {
        value = `${parts[0]}.${parts.slice(1).join('')}`;
      }
    } else {
      value = value.replace(/\D/g, '');
    }
    if (target.value !== value) {
      target.value = value;
    }
  }
  
  emit('update:modelValue', value);
};
</script>

<style lang="scss" scoped>
@import './cms-styles.scss';

.cms-input-embedded {
  margin-bottom: 0;
  min-width: 0;
  width: 100%;
}

.cms-input-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.cms-input-label-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.2rem;
}

.cms-help-tooltip-wrapper {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

.cms-help-tooltip {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: help;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

.cms-help-icon {
  color: #666 !important;
  opacity: 0.7 !important;
  transition: opacity 0.2s, color 0.2s;
  display: inline-block !important;
  flex-shrink: 0;
  cursor: help;

  &:hover {
    opacity: 1 !important;
    color: #333 !important;
  }
}

.cms-tooltip-content {
  position: absolute;
  bottom: calc(100% + 10px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 10000;
  min-width: 280px;
  max-width: 400px;
  pointer-events: none;
  animation: tooltipFadeIn 0.2s ease-out;
  
  // Ensure tooltip doesn't get cut off
  @media (max-width: 768px) {
    left: auto;
    right: 0;
    transform: none;
    min-width: 250px;
    max-width: calc(100vw - 40px);
  }
}

@keyframes tooltipFadeIn {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

.cms-tooltip-arrow {
  position: absolute;
  bottom: -6px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 6px solid rgba(0, 0, 0, 0.9);
  filter: drop-shadow(0 2px 2px rgba(0, 0, 0, 0.2));
}

.cms-tooltip-text {
  background: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 12px 16px;
  border-radius: 6px;
  font-size: $cms-font-size-xs;
  line-height: 1.5;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  white-space: normal;
  text-align: left;

  :deep(p) {
    margin: 0 0 8px 0;
    
    &:last-child {
      margin-bottom: 0;
    }
  }

  :deep(strong) {
    font-weight: 600;
  }

  :deep(ul) {
    margin: 8px 0 0 0;
    padding-left: 20px;
    
    li {
      margin-bottom: 4px;
    }
  }
}
</style>



